//src/pages/layout/Sidebar.jsx
import { useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import GroupIcon from '@mui/icons-material/Group'; // For Manage Your Team
import KingBedIcon from '@mui/icons-material/KingBed'; // For Manage Wards / Beds
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BuildIcon from '@mui/icons-material/Build';
import AddNewDoctor from "../team/Doctors/AddNewDoctor.jsx";
import StaffModal from "../team/Roles/EditRole.jsx";
import { useAuth } from "../../context/AuthContext"; 
import { getDoctorById, getDoctorByUserId } from "../../services/doctorService.js";
import {getStaffByUserId } from "../../services/StaffService.js";
import { getOrganizationName, fetchAndStoreOrganizationName, getOrganizationId, addOrgToUrl } from "../../utils/organizationUtils";
import { formatRoleDisplayName, isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";

const Item = ({ title, to, icon, selected, setSelected, user, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  // Add organization prefix to the path if user has organization
  const pathWithOrg = addOrgToUrl(to, user);

  const handleClick = () => {
    setSelected(title);
    navigate(pathWithOrg);
  };

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={handleClick}
      icon={icon}
    >
      {!isCollapsed && (
        <Typography sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{title}</Typography>
      )}
    </MenuItem>
  );
};

const Sidebar = ({ setIsCollapsed, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const { hasCapability, isPremiumPackage, hasPathPackageAccess } = useSubscriptionCapabilities();
  
  // Check capabilities for menu item visibility
  const hasWardsBedsCapability = hasCapability("Wards") || hasCapability("Beds") || hasCapability("IPD");
  const hasMedicineInventoryCapability = hasCapability("Medicine Inventory");
  const hasInvoicesCapability = hasCapability("Invoices");
  // Attendance History is Premium-only (Basic / Intermediate excluded)
  const hasAttendanceCapability = isPremiumPackage;
  
  // console.log(user, 'authuser'); 
  const [selected, setSelected] = useState("Dashboard");
  const [doctor, setDoctor] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open_doctor_modal, setOpenDoctorModal] = useState(false);
  const [open_staff_modal, setOpenStaffModal] = useState(false);
  const [organizationName, setOrganizationName] = useState(null); // State for organization name
  const [profileCompletion, setProfileCompletion] = useState(0); // Profile completion percentage
  
  // Listen for organizationId updates (for super_admin who gets organizationId after login)
  useEffect(() => {
    const handleOrganizationIdUpdate = (event) => {
      const { updatedUser } = event.detail;
      if (updatedUser) {
        console.log("[Sidebar] OrganizationId updated event received, user object updated");
        // The user object from useAuth will be updated by AuthContext
        // This effect ensures sidebar re-renders when organizationId is set
      }
    };
    
    window.addEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);
    
    return () => {
      window.removeEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);
    };
  }, []);

  // Calculate profile completion percentage for doctor
  const calculateDoctorProfileCompletion = (doctorData) => {
    if (!doctorData) {
      setProfileCompletion(0);
      return;
    }

    const requiredFields = [
      'name',
      'email',
      'education',
      'specialty',
      'aadhar',
      'mobile',
      'registrationNumber',
      'departmentId',
      'joiningDate',
      'address',
      'userRole',
    ];

    // Check visiting schedules (at least one schedule is required)
    // visitingSchedules is an object with day names as keys (e.g., { "monday": { timings: [...] }, "tuesday": { timings: [...] } })
    let hasVisitingSchedules = false;
    if (doctorData.visitingSchedules && typeof doctorData.visitingSchedules === 'object') {
      // Check if it's an object with at least one day that has timings
      if (Array.isArray(doctorData.visitingSchedules)) {
        // Handle array format (legacy format)
        hasVisitingSchedules = doctorData.visitingSchedules.length > 0;
      } else {
        // Handle object format (current format: { "monday": { timings: [...] }, ... })
        const dayKeys = Object.keys(doctorData.visitingSchedules);
        hasVisitingSchedules = dayKeys.some(day => {
          const schedule = doctorData.visitingSchedules[day];
          return schedule && (
            (Array.isArray(schedule.timings) && schedule.timings.length > 0) ||
            (Array.isArray(schedule.times) && schedule.times.length > 0) ||
            (schedule.startTime && schedule.endTime)
          );
        });
      }
    }

    let completedFields = 0;
    const totalFields = requiredFields.length + 1; // +1 for visiting schedules

    requiredFields.forEach(field => {
      const value = doctorData[field];
      if (value !== null && value !== undefined && value !== '' && 
          !(Array.isArray(value) && value.length === 0)) {
        completedFields++;
      }
    });

    // Add visiting schedules check
    if (hasVisitingSchedules) {
      completedFields++;
    }

    const percentage = Math.round((completedFields / totalFields) * 100);
    setProfileCompletion(percentage);
  };

  // Calculate profile completion percentage for staff/other roles
  const calculateStaffProfileCompletion = (staffData) => {
    if (!staffData) {
      setProfileCompletion(0);
      return;
    }

    const requiredFields = [
      'name',
      'email',
      'education',
      'aadhar',
      'mobile',
      'joiningDate',
      'address',
      'userRole',
    ];

    let completedFields = 0;
    const totalFields = requiredFields.length;

    requiredFields.forEach(field => {
      const value = staffData[field];
      if (value !== null && value !== undefined && value !== '' && 
          !(Array.isArray(value) && value.length === 0)) {
        completedFields++;
      }
    });

    const percentage = Math.round((completedFields / totalFields) * 100);
    setProfileCompletion(percentage);
  };

  const handleOpenProfileUpdate = () => {
    const role = (user?.roleName || "").toLowerCase();
    if (role === "doctor" || role === "surgeon") {
      setOpenDoctorModal(true);
    } else {
      // For all other roles (staff, admin, superadmin, frontdesk, pathdesk, pharmacy admin, etc.)
      // Use EditRole modal which works for all staff-based roles
      setOpenStaffModal(true);
    }
  };

  useEffect(() => {
    // Get userId from localStorage (which has the correct userId updated during login)
    // The user object from AuthContext might have the wrong userId if login API returned wrong user
    const storedUser = localStorage.getItem("user");
    let userId = user?.id;
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.id) {
          userId = parsedUser.id;
          console.log(`[Sidebar] Using userId ${userId} from localStorage (corrected from login)`);
        }
      } catch (e) {
        console.error("[Sidebar] Error parsing stored user, using user.id from context:", e);
      }
    }
    
    if (user?.roleName === 'doctor' || user?.roleName === 'surgeon') {  
      const fetchDoctorById = async () => {
        try {
          const data = await getDoctorByUserId(userId);
          setDoctor(data);
          // Calculate profile completion for doctor
          calculateDoctorProfileCompletion(data);
        } catch (error) {
          console.log(error);
          setProfileCompletion(0);
        }
      };
      fetchDoctorById(); 
    } else {
      // For all other roles (staff, admin, superadmin, frontdesk, pathdesk, pharmacy admin, etc.)
      // Try to fetch staff data - most roles use the staff table
      const fetchStaffById = async () => {
        try {
          const data = await getStaffByUserId(userId);
          setStaff(data);
          // Calculate profile completion for staff/other roles
          calculateStaffProfileCompletion(data);
        } catch (error) {
          // Some users (like admin/superadmin) might not be in staff table - that's okay
          // Log warning but continue without staff details
          const role = (user?.roleName || "").toLowerCase();
          if (role === 'admin' || role === 'superadmin' || role === 'super_admin') {
            console.warn(`[Sidebar] ${user?.roleName} user not found in staff table, continuing without staff details`);
          } else {
            console.warn(`[Sidebar] Error fetching staff details for ${user?.roleName}:`, error);
          }
          setProfileCompletion(0);
        }
      };
      fetchStaffById(); 
    }
  }, [user]);

  const profileRingColor =
    profileCompletion === 100
      ? "#4caf50"
      : profileCompletion >= 50
      ? "#ff9800"
      : "#f44336";
  const profileProgressAngle = profileCompletion * 3.6 - 90;
  const profileProgressRadians = (profileProgressAngle * Math.PI) / 180;
  const profileRingCenter = 54;
  const profileRingLabelRadius = 52;
  const profileProgressLabelX =
    profileRingCenter + Math.cos(profileProgressRadians) * profileRingLabelRadius;
  const profileProgressLabelY =
    profileRingCenter + Math.sin(profileProgressRadians) * profileRingLabelRadius;

  // Effect to load organization name
  useEffect(() => {
    const loadOrgName = () => {
      if (user) {
        // Always check localStorage directly first (in case it was updated after user prop was set)
        const storedUser = localStorage.getItem("user");
        let orgName = null;
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            orgName = parsedUser?.organizationName || null;
          } catch (e) {
            console.error("[Sidebar] Error parsing stored user:", e);
          }
        }
        
        // If not in localStorage, try to get from user prop (which uses getOrganizationName)
        if (!orgName) {
          orgName = getOrganizationName(user);
        }
        
        if (orgName) {
          setOrganizationName(orgName);
        } else {
          // Try to fetch if organizationId exists but name is missing
          // For super_admin, also check localStorage.organizationId as fallback
          let orgId = getOrganizationId(user);
          if (!orgId) {
            const orgIdFromStorage = localStorage.getItem("organizationId");
            if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
              orgId = Number(orgIdFromStorage);
              console.log(`[Sidebar] Using organizationId from localStorage fallback: ${orgId}`);
            }
          }
          if (orgId) {
            fetchAndStoreOrganizationName(orgId).then((name) => {
              if (name) {
                setOrganizationName(name);
              }
            }).catch((error) => {
              console.warn("[Sidebar] Could not fetch organization name:", error);
            });
          } else {
            // No organization - will show HMSPro
            setOrganizationName(null);
          }
        }
      } else {
        setOrganizationName(null);
      }
    };

    // Load immediately
    loadOrgName();

    // Listen for custom event when user is updated in localStorage
    const handleUserUpdate = (event) => {
      const updatedUser = event.detail?.updatedUser;
      if (updatedUser?.organizationName) {
        setOrganizationName(updatedUser.organizationName);
      } else {
        loadOrgName();
      }
      // Re-fetch staff/doctor profile so sidebar profile data stays in sync after hospital edit
      if (updatedUser?.username || updatedUser?.userName) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            const userId = parsed?.id;
            const role = (parsed?.roleName || "").toLowerCase();
            if (userId && (role === "doctor" || role === "surgeon")) {
              getDoctorByUserId(userId)
                .then((data) => {
                  setDoctor(data);
                  calculateDoctorProfileCompletion(data);
                })
                .catch(() => {});
            } else if (userId) {
              getStaffByUserId(userId)
                .then((data) => {
                  setStaff(data);
                  calculateStaffProfileCompletion(data);
                })
                .catch(() => {});
            }
          } catch (e) {
            console.warn("[Sidebar] Could not refresh profile after user update:", e);
          }
        }
      }
    };

    window.addEventListener('userUpdated', handleUserUpdate);

    // Also re-check after a short delay to catch any updates that happen after initial render
    // This is especially important for frontdesk users where organizationName is fetched during login
    const timeoutId = setTimeout(() => {
      loadOrgName();
    }, 500); // Check after 500ms

    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
      clearTimeout(timeoutId);
    };
  }, [user]);

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: 1000,
        height: "100%",
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 12px 5px 10px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed} width="216px">
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography
                  variant="h3"
                  fontWeight={"bold"}
                  color={theme.palette.primary.main}
                >
                  {organizationName || "HMSPro"}
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <Tooltip title="Please complete profile" arrow>
                  <Box
                    onClick={handleOpenProfileUpdate}
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      width: "108px",
                      height: "108px",
                      cursor: "pointer",
                      borderRadius: "50%",
                      background: `conic-gradient(from 0deg, ${profileRingColor} 0deg, ${profileRingColor} ${profileCompletion * 3.6}deg, #e0e0e0 ${profileCompletion * 3.6}deg, #e0e0e0 360deg)`,
                      p: "4px",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.18)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        alt="profile-user"
                        width="100px"
                        height="100px"
                        src={`../../assets/boy.png`}
                        style={{
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    >
                      <svg width="108" height="108" viewBox="0 0 108 108" style={{ overflow: "visible" }}>
                        <text
                          x={profileProgressLabelX}
                          y={profileProgressLabelY}
                          fill={profileRingColor}
                          fontSize="10"
                          fontWeight="700"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{
                            paintOrder: "stroke",
                            stroke: "rgba(255, 255, 255, 0.95)",
                            strokeWidth: 1.5,
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                          }}
                        >
                          {profileCompletion}%
                        </text>
                      </svg>
                    </Box>
                  </Box>
                </Tooltip>
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={theme.palette.primary.main}
                  fontWeight="bold"
                  sx={{
                    m: "10px 0 0 0",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={handleOpenProfileUpdate}
                >
                  {user?.username}
                </Typography>

                <Typography variant="h5" color={theme.palette.primary.main}>
                  {formatRoleDisplayName(user?.roleName).toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "3%"}>
            {(user?.roleName === "doctor" ||
              (isPathModuleStaffRole(user?.roleName) && hasPathPackageAccess)) && (
              <>
                <Item
                  title="Dashboard"
                  to="/"
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
              </>
            )}
            {(user?.roleName === "doctor" || user?.roleName === "surgeon") && (
              <>
                <Item
                  title="Manage Your Patients"
                  to="/manage-patient"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Invoices Balances"
                  to="/invoice"
                  icon={<MonetizationOnIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                {hasAttendanceCapability && (
                  <Item
                    title="Attendance History"
                    to="/attendance-history"
                    icon={<EventNoteIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
              </>
            )}
            {isPathModuleStaffRole(user?.roleName) && hasPathPackageAccess && (
              <>
                <Item
                  title="Manage Your Patients"
                  to="/path-patient"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Inventory Information"
                  to="/inventory"
                  icon={<InventoryIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Invoices Balances"
                  to="/invoice"
                  icon={<MonetizationOnIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
              </>
            )}
            {user?.roleName === "frontdesk" && (
              <>
                <Item
                  title="Dashboard"
                  to="/token-dashboard"
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Manage Your Team"
                  to="/team"
                  icon={<GroupIcon  />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Manage Patients"
                  to="/front-desk"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                {hasWardsBedsCapability && (
                  <Item
                    title="Manage Wards / Beds"
                    to="/wards-beds"
                    icon={<KingBedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                {hasInvoicesCapability && (
                  <Item
                    title="Invoices Balances"
                    to="/invoice"
                    icon={<MonetizationOnIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                {hasAttendanceCapability && (
                  <Item
                    title="Attendance History"
                    to="/attendance-history"
                    icon={<EventNoteIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
              </>
            )}
            {(() => {
              const role = (user?.roleName || "").toLowerCase();
              return role === "admin" || role === "superadmin" || role === "super_admin";
            })() && (
              <>
                <Item
                  title="Dashboard"
                  to="/"
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Manage Your Team"
                  to="/team"
                  icon={<GroupIcon  />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Manage Patients"
                  to="/front-desk"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                {hasWardsBedsCapability && (
                  <Item
                    title="Manage Wards / Beds"
                    to="/wards-beds"
                    icon={<KingBedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                {hasMedicineInventoryCapability && (
                  <Item
                    title="Inventory Information"
                    to="/inventory"
                    icon={<InventoryIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                {hasInvoicesCapability && (
                  <Item
                    title="Invoices Balances"
                    to="/invoice"
                    icon={<MonetizationOnIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                {hasAttendanceCapability && (
                  <Item
                    title="Attendance History"
                    to="/attendance-history"
                    icon={<EventNoteIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                <Item
                  title="Organization Utilities"
                  to="/organization-utilities"
                  icon={<BuildIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
              </>
            )}
            {user?.roleName === "staff" && (
              <>
                {hasAttendanceCapability && (
                  <Item
                    title="Attendance History"
                    to="/attendance-history"
                    icon={<EventNoteIcon />}
                    selected={selected}
                    setSelected={setSelected}
                    user={user}
                    isCollapsed={isCollapsed}
                  />
                )}
                <Item
                  title="Manage Patients"
                  to="/front-desk"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
              </>
            )}

           {user?.roleName === "pharmacy admin" && (
              <>
                <Item
                  title="Dashboard"
                  to="/"
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Dispense Medicines"
                  to="/manage-patient"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Inventory Information"
                  to="/inventory"
                  icon={<InventoryIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Invoices Balances"
                  to="/invoice"
                  icon={<MonetizationOnIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  user={user}
                  isCollapsed={isCollapsed}
                />
              </>
              )}

          </Box>
        </Menu>
      </ProSidebar>
      <AddNewDoctor
        open={open_doctor_modal}
        onClose={async () => {
          setOpenDoctorModal(false);
          // Refresh doctor data and recalculate profile completion
          if (user?.roleName === 'doctor' || user?.roleName === 'surgeon') {
            const storedUser = localStorage.getItem("user");
            let userId = user?.id;
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser?.id) {
                  userId = parsedUser.id;
                }
              } catch (e) {
                console.error("[Sidebar] Error parsing stored user:", e);
              }
            }
            try {
              const data = await getDoctorByUserId(userId);
              setDoctor(data);
              calculateDoctorProfileCompletion(data);
            } catch (error) {
              console.log(error);
            }
          }
        }}
        doctor_info={doctor}
      />
      <StaffModal
        open={open_staff_modal}
        onClose={async () => {
          setOpenStaffModal(false);
          // Refresh staff data and recalculate profile completion
          const storedUser = localStorage.getItem("user");
          let userId = user?.id;
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              if (parsedUser?.id) {
                userId = parsedUser.id;
              }
            } catch (e) {
              console.error("[Sidebar] Error parsing stored user:", e);
            }
          }
          try {
            const data = await getStaffByUserId(userId);
            setStaff(data);
            calculateStaffProfileCompletion(data);
          } catch (error) {
            console.warn(`[Sidebar] Error refreshing staff details:`, error);
          }
        }}
        doctor_info={staff}
      />
    </Box>
  );
};

export default Sidebar;